/**
 * Utility functions for processing Merchant IDs (MIDs) with prefixes
 */

/**
 * Splits a comma-separated string of IDs and adds a prefix to each
 * @param {string} value - Comma-separated string of IDs
 * @param {string} prefix - Prefix to add (e.g., 'SPC_', 'SPL_', 'HN_')
 * @returns {string[]} Array of prefixed IDs
 */
export const splitAndPrefix = (value, prefix) => {
  if (!value) return []
  return value
    .split(',')
    .map(id => id.trim())
    .filter(Boolean)
    .map(id => `${prefix}${id}`)
}

/**
 * Processes an array of MIDs with prefixes and separates them by type
 * @param {string[]} midList - Array of MIDs with prefixes (e.g., ['SPC_123', 'SPL_456', 'HN_789'])
 * @returns {object} Object with separated and stripped IDs:
 *   - spadeCounterPartyId: comma-separated string of SPC_ IDs (prefixes stripped)
 *   - spadeLocationId: comma-separated string of SPL_ IDs (prefixes stripped)
 *   - highnoteId: comma-separated string of HN_ IDs (prefixes stripped)
 */
export const processMidList = midList => {
  if (!midList || midList.length === 0) {
    return {
      spadeCounterPartyId: '',
      spadeLocationId: '',
      highnoteId: '',
    }
  }

  const spadeCounterPartyIds = midList.filter(mid => mid.startsWith('SPC_')).map(mid => mid.replace(/^SPC_/, ''))
  const spadeLocationIds = midList.filter(mid => mid.startsWith('SPL_')).map(mid => mid.replace(/^SPL_/, ''))
  const highnoteIds = midList.filter(mid => mid.startsWith('HN_')).map(mid => mid.replace(/^HN_/, ''))

  return {
    spadeCounterPartyId: spadeCounterPartyIds.join(','),
    spadeLocationId: spadeLocationIds.join(','),
    highnoteId: highnoteIds.join(','),
  }
}

/**
 * Processes a comma-separated string of MIDs with prefixes and separates them by type
 * @param {string} midsString - Comma-separated string of MIDs with prefixes (e.g., 'SPC_123,SPL_456,HN_789')
 * @returns {object} Object with separated and stripped IDs:
 *   - spadeCounterPartyId: comma-separated string of SPC_ IDs (prefixes stripped)
 *   - spadeLocationId: comma-separated string of SPL_ IDs (prefixes stripped)
 *   - highnoteId: comma-separated string of HN_ IDs (prefixes stripped)
 */
export const processMidString = midsString => {
  if (!midsString) {
    return {
      spadeCounterPartyId: '',
      spadeLocationId: '',
      highnoteId: '',
    }
  }

  // Split comma-separated string into array
  const midList = midsString
    .split(',')
    .map(mid => mid.trim())
    .filter(Boolean)
  return processMidList(midList)
}
